/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropStats } from "../../store/useDropStats";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { PrimaryButton } from "../buttons/PrimaryButton";
import { useMarketPricing } from "../../store/useMarketPricing";
import DropdownFilter from "../modals/DropdownFilter";
import { TimelineChart } from "./Timeline.chart";

interface Props {
  selectedFiltersProp?: string[];
}

// Modified main component
export const AverageDaysBetweenDrops = memo(
  ({ selectedFiltersProp }: Props) => {
    /** Refs */
    const snapshotRef = useRef(null);
    /** Hooks */
    const { averageDaysBetweenDrops, getAverageDaysBetweenDrops } =
      useDropStats();
    const { marketPrices, getAllMarketPrices } = useMarketPricing();
    const [_, takeScreenshot] = useScreenshot();
    /** State */
    const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const [periodYear /* setPeriodYear */] = useState<number>(currentYear);

    /** Callbacks */
    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        const link = document.createElement("a");
        link.href = image;
        link.download = "average_days_between_drops.png";
        link.click();
      });
    };

    const filterOptions = useMemo(() => {
      const kgOptions = marketPrices?.filter((mp) => mp?.measurement === "kg");
      return [
        "All",
        ...(kgOptions?.length ? kgOptions?.map((price) => price.name) : []),
        "Total",
      ];
    }, [marketPrices]);

    const transformedData = useMemo(() => {
      if (!averageDaysBetweenDrops) return [];

      // Filter series based on selected filters
      const filteredSeries = averageDaysBetweenDrops.filter((series) =>
        selectedFilters.includes(series.name?.replaceAll(" Active Users", ""))
      );

      return filteredSeries;
    }, [averageDaysBetweenDrops, selectedFilters]);

    const onSelectFilters = useCallback(
      (option: string) => {
        if (option === "All") {
          if (selectedFilters.includes("All")) {
            setSelectedFilters([]);
            return;
          }
          setSelectedFilters([
            "All",
            ...(marketPrices?.length
              ? marketPrices?.map((price) => price.name)
              : []),
            "Total",
          ]);
          return;
        }
        setSelectedFilters((prev: any) =>
          prev.includes(option)
            ? prev
                .filter((item: any) => item !== option)
                ?.filter((item: any) => item !== "All")
            : [...prev, option]?.filter((item: any) => item !== "All")
        );
      },
      [marketPrices, selectedFilters, filterOptions]
    );

    /** Effects */
    useEffect(() => {
      if (selectedFiltersProp) setSelectedFilters(selectedFiltersProp);
    }, [selectedFiltersProp]);

    useEffect(() => {
      getAllMarketPrices();
    }, []);

    useEffect(() => {
      setSelectedFilters([
        "All",
        ...(marketPrices?.length
          ? marketPrices?.map((price) => price.name)
          : []),
        "Total",
      ]);
    }, [marketPrices]);

    useEffect(() => {
      getAverageDaysBetweenDrops();
    }, [periodYear]);

    return (
      <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl border-gray-border drop-shadow-none mb-1 sm:mb-10">
        <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-12">
            Average Days Between Drops
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end">
            <DropdownFilter
              label="Filters"
              options={filterOptions}
              selectedOptions={selectedFilters}
              onSelect={onSelectFilters}
            />
            <div className="ml-0 sm:ml-2" />
            <PrimaryButton
              size="sm"
              onClick={downloadScreenshot}
              label="Download image"
            />
          </div>
        </div>
        <div ref={snapshotRef} className="w-full">
          {transformedData?.length ? (
            <div className="flex flex-col space-y-8">
              <TimelineChart data={transformedData} />
              <p className="text-center text-gray-600 text-sm">
                The line shows the average number of days people take between
                two drops for every stream.
              </p>
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <p className="mb-[100px] font-satoshi font-bold text-2xl text-tailgrids-black">
                No data found
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
);
